import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Activity, TrendingUp, ShieldCheck, Package, Snowflake, ChefHat, Star, Users,
  Award, Clock, AlertTriangle, Loader2, RefreshCw, CheckCircle, XCircle, Zap
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const REFRESH_MS = 30000; // 30 sec

export function RealtimeDashboardPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const intervalRef = useRef(null);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get('/realtime/dashboard');
      setData(res.data);
      setLastRefresh(new Date());
    } catch {
      if (!silent) toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => fetchData(true), REFRESH_MS);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, fetchData]);

  if (loading || !data) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  }

  const { revenus, hygiene, stock, decongelation, traiteur, fidelite, planning, formation, activite_recente } = data;

  const colorText = { green: 'text-green-600', blue: 'text-blue-600', amber: 'text-amber-600', red: 'text-red-600' };
  const colorBg = { green: 'bg-green-50 border-green-300', blue: 'bg-blue-50 border-blue-300', amber: 'bg-amber-50 border-amber-300', red: 'bg-red-50 border-red-300' };

  const totalAlertes = hygiene.nc_today_total + hygiene.dlc_expirees + (hygiene.huiles_alerte?.length || 0) + stock.rupture + (decongelation.alertes?.length || 0);

  return (
    <div className="space-y-4" data-testid="realtime-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="w-6 h-6 text-green-600 animate-pulse" />
            Tableau de bord temps reel
          </h1>
          <p className="text-muted-foreground text-sm">
            {data.today} — Semaine {data.semaine} — Derniere maj: {lastRefresh?.toLocaleTimeString('fr-FR')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={autoRefresh ? 'default' : 'outline'} size="sm" onClick={() => setAutoRefresh(a => !a)} data-testid="toggle-auto-refresh">
            <Zap className={`w-4 h-4 mr-1 ${autoRefresh ? 'text-yellow-300' : ''}`} />
            Auto {autoRefresh ? 'ON' : 'OFF'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => fetchData()} data-testid="manual-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Rafraichir
          </Button>
        </div>
      </div>

      {/* Big alert banner if problems */}
      {totalAlertes > 0 && (
        <Card className="border-2 border-red-300 bg-red-50/50 animate-in fade-in" data-testid="global-alert-banner">
          <CardContent className="py-3 flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            <div className="flex-1">
              <p className="font-bold text-red-700">{totalAlertes} alerte{totalAlertes > 1 ? 's' : ''} active{totalAlertes > 1 ? 's' : ''}</p>
              <p className="text-xs text-red-600/80 mt-0.5">
                {hygiene.nc_today_total > 0 && <span className="mr-2">{hygiene.nc_today_total} NC hygiene</span>}
                {hygiene.dlc_expirees > 0 && <span className="mr-2">{hygiene.dlc_expirees} DLC expirees</span>}
                {hygiene.huiles_alerte?.length > 0 && <span className="mr-2">Huiles: {hygiene.huiles_alerte.join(', ')}</span>}
                {stock.rupture > 0 && <span className="mr-2">{stock.rupture} ruptures stock</span>}
                {decongelation.alertes?.length > 0 && <span className="mr-2">{decongelation.alertes.length} decongeles DLC proche</span>}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <BigTile
          onClick={() => navigate('/ca-reel')}
          icon={TrendingUp} color="text-green-600"
          label="CA aujourd'hui"
          value={`${revenus.ca_today.toFixed(0)}€`}
          sub={`${revenus.nb_tickets_today} tickets`}
        />
        <BigTile
          onClick={() => navigate('/dashboard-kpi')}
          icon={TrendingUp} color="text-blue-600"
          label="CA mois"
          value={`${(revenus.ca_mois / 1000).toFixed(1)}k€`}
          sub={`${revenus.pct_realisation}% du forecast`}
          pct={revenus.pct_realisation}
        />
        <BigTile
          onClick={() => navigate('/hygiene')}
          icon={ShieldCheck} color={colorText[hygiene.audit_couleur]}
          label="Score HACCP"
          value={`${hygiene.audit_score}/100`}
          sub={hygiene.audit_niveau}
          highlight={colorBg[hygiene.audit_couleur]}
        />
        <BigTile
          onClick={() => navigate('/inventaire')}
          icon={Package} color={stock.rupture > 0 ? 'text-red-600' : stock.alerte > 0 ? 'text-amber-600' : 'text-green-600'}
          label="Stock"
          value={stock.total}
          sub={stock.rupture > 0 ? `${stock.rupture} ruptures` : stock.alerte > 0 ? `${stock.alerte} en alerte` : 'OK'}
          alert={stock.rupture > 0}
        />
        <BigTile
          onClick={() => navigate('/planning')}
          icon={Users} color="text-purple-600"
          label="Equipe du jour"
          value={planning.total_today}
          sub={planning.jour}
        />
        <BigTile
          onClick={() => navigate('/traiteur')}
          icon={ChefHat} color="text-orange-600"
          label="Traiteur"
          value={traiteur.today}
          sub={`${traiteur.total_semaine} sur 7j`}
        />
      </div>

      {/* Middle row: details */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Hygiene details */}
        <Card className={hygiene.nc_today_total > 0 ? 'border-red-200' : ''}>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-green-600" /> Hygiene aujourd'hui</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Releves frigo" value={hygiene.releves_frigo_today} alert={hygiene.non_conformes_frigo > 0} alertText={hygiene.non_conformes_frigo > 0 ? `${hygiene.non_conformes_frigo} NC` : null} />
            <Row label="Releves produits" value={hygiene.releves_produit_today} alert={hygiene.non_conformes_produit > 0} alertText={hygiene.non_conformes_produit > 0 ? `${hygiene.non_conformes_produit} NC` : null} />
            <Row label="DLC expirees" value={hygiene.dlc_expirees} alert={hygiene.dlc_expirees > 0} />
            <Row label="DLC en alerte" value={hygiene.dlc_alerte} alert={hygiene.dlc_alerte > 0} warning />
            <Row label="Huiles a changer" value={hygiene.huiles_alerte?.length || 0} alert={hygiene.huiles_alerte?.length > 0} alertText={hygiene.huiles_alerte?.join(', ')} />
            <Row label="Certificats valides (mois)" value={formation.certifs_mois} icon={Award} iconColor="text-amber-600" />
          </CardContent>
        </Card>

        {/* Stock details */}
        <Card className={stock.rupture > 0 ? 'border-red-200' : ''}>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Package className="w-4 h-4 text-blue-600" /> Stock critique</CardTitle></CardHeader>
          <CardContent className="space-y-1.5 text-sm">
            {stock.rupture === 0 && stock.alerte === 0 ? (
              <p className="text-muted-foreground text-center py-4"><CheckCircle className="w-5 h-5 inline text-green-600 mr-1" /> Tout OK</p>
            ) : (
              <>
                {stock.rupture_items?.slice(0, 5).map((it, i) => (
                  <div key={i} className="flex items-center justify-between p-1.5 rounded bg-red-50/50 border border-red-100">
                    <div className="min-w-0"><p className="font-medium truncate">{it.ingredient}</p><p className="text-xs text-muted-foreground">{it.zone}</p></div>
                    <Badge className="bg-red-500 text-white">Rupture</Badge>
                  </div>
                ))}
                {stock.alerte_items?.slice(0, 3).map((it, i) => (
                  <div key={`a${i}`} className="flex items-center justify-between p-1.5 rounded bg-amber-50/50 border border-amber-100">
                    <div className="min-w-0"><p className="font-medium truncate">{it.ingredient}</p><p className="text-xs text-muted-foreground">{it.zone}</p></div>
                    <Badge variant="outline" className="border-amber-500 text-amber-700">{it.stock} / {it.seuil}</Badge>
                  </div>
                ))}
              </>
            )}
          </CardContent>
        </Card>

        {/* Decongelation + Traiteur */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Snowflake className="w-4 h-4 text-cyan-600" /> Decongele & Traiteur</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Produits decongeles actifs" value={decongelation.actifs} alert={decongelation.alertes?.length > 0} />
            {decongelation.alertes?.slice(0, 3).map((a, i) => (
              <div key={i} className="pl-4 text-xs text-amber-700">→ {a.produit} — DLC {a.dlc}</div>
            ))}
            <div className="border-t pt-2 mt-2 space-y-1">
              <p className="font-medium text-xs text-muted-foreground">Prochaines commandes traiteur</p>
              {traiteur.semaine.length === 0 ? <p className="text-xs text-muted-foreground italic">Aucune</p> :
                traiteur.semaine.slice(0, 3).map((t, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span><b>{t.date}</b> — {t.client}</span>
                    <span className="text-muted-foreground">{t.nb_personnes} pers • {t.montant}€</span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        {/* Equipe jour */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4 text-purple-600" /> Equipe du jour ({planning.jour})</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {planning.employes_today.length === 0 ? <p className="text-xs text-muted-foreground">Aucun planning pour aujourd'hui</p> :
              planning.employes_today.map((e, i) => (
                <div key={i} className="flex items-center justify-between text-sm p-1.5 rounded bg-purple-50/30">
                  <span className="font-medium">{e.nom}</span>
                  <span className="text-xs text-muted-foreground">{e.horaires} • {e.heures}h</span>
                </div>
              ))}
          </CardContent>
        </Card>

        {/* Fidelite */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Star className="w-4 h-4 text-yellow-600" /> Fidelite</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Nouveaux (7j)" value={`+${fidelite.nouveaux_semaine}`} />
            <Row label="Total clients" value={fidelite.total_clients} />
          </CardContent>
        </Card>

        {/* Activite recente */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-slate-600" /> Activite recente</CardTitle></CardHeader>
          <CardContent className="space-y-1.5 text-xs">
            {activite_recente.length === 0 ? <p className="text-muted-foreground italic">Aucune activite</p> :
              activite_recente.map((a, i) => {
                const ts = a.created_at ? new Date(a.created_at) : null;
                return (
                  <div key={i} className="flex items-center gap-2 py-0.5 border-b last:border-0">
                    <Badge variant="outline" className="text-[9px] py-0">{a.label}</Badge>
                    <span className="flex-1 truncate">{a.detail || '-'}</span>
                    <span className="text-muted-foreground whitespace-nowrap">{ts ? ts.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                  </div>
                );
              })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function BigTile({ icon: Icon, label, value, sub, color = 'text-slate-600', onClick, highlight, alert, pct }) {
  return (
    <Card
      className={`cursor-pointer transition hover:shadow-md hover:-translate-y-0.5 ${highlight || ''} ${alert ? 'border-red-300' : ''}`}
      onClick={onClick}
    >
      <CardContent className="pt-3 pb-2.5">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-muted-foreground">{label}</span>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
        <p className={`text-xl md:text-2xl font-bold tabular-nums ${color}`}>{value}</p>
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
        {pct != null && (
          <div className="h-1 mt-1.5 bg-muted rounded-full overflow-hidden">
            <div className={`h-full ${pct >= 100 ? 'bg-green-500' : pct >= 80 ? 'bg-blue-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Row({ label, value, alert, alertText, warning, icon: Icon, iconColor }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`flex items-center gap-1.5 ${alert ? 'text-red-700' : warning ? 'text-amber-700' : ''}`}>
        {Icon && <Icon className={`w-4 h-4 ${iconColor || ''}`} />}
        {label}
      </span>
      <span className={`flex items-center gap-1.5 font-semibold tabular-nums ${alert ? 'text-red-600' : warning ? 'text-amber-600' : ''}`}>
        {value}
        {alert && alertText && <Badge className="bg-red-500 text-white text-[10px]">{alertText}</Badge>}
        {alert && !alertText && <XCircle className="w-3.5 h-3.5 text-red-600" />}
        {!alert && !warning && value === 0 && <CheckCircle className="w-3.5 h-3.5 text-green-600" />}
      </span>
    </div>
  );
}
